import { useEffect, useState } from 'react';

const HEX_COLOR_RE = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

/**
 * Expands "#abc" to "#aabbcc" — the native color input only accepts the
 * six-digit form.
 *
 * @param {string} hex
 * @returns {string}
 */
const expandHex = (hex) => {
  const h = (hex || '').trim();
  return /^#[0-9A-Fa-f]{3}$/.test(h) ? '#' + h.slice(1).split('').map(c => c + c).join('') : h;
};

/**
 * A hex color field: native color-swatch picker + editable hex text input,
 * kept in local "draft" state so the admin can type freely — the value only
 * reaches `onChange` once the draft is a valid hex colour.
 *
 * Shared by Config -> Theme's Site Colors and Time Slot Button Colors.
 *
 * @param {object} props
 * @param {string} props.label
 * @param {string} [props.hint] - small helper line under the label
 * @param {string} props.value - the current valid hex value
 * @param {(value: string) => void} props.onChange - called with a valid hex
 * @param {boolean} [props.disabled] - shows the value read-only (e.g. while
 *   a colour is following another setting)
 * @returns {JSX.Element}
 */
function ColorField({ label, hint, value, onChange, disabled = false }) {
  const [draft, setDraft] = useState(value);

  useEffect(() => { setDraft(value); }, [value]);

  const isValid = HEX_COLOR_RE.test(draft.trim());

  /**
   * Updates the draft, and the value too once it is a valid hex.
   * @param {string} next
   */
  const commit = (next) => {
    setDraft(next);
    if (HEX_COLOR_RE.test(next.trim())) {
      onChange(next.trim());
    }
  };

  return (
    <div className="form-group" style={{ marginBottom: 0 }}>
      <label>{label}</label>
      {hint && <p style={{ margin: '0 0 0.4rem', fontSize: '0.72rem', opacity: 0.6 }}>{hint}</p>}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <input
          type="color"
          value={expandHex(isValid ? draft : value)}
          onChange={(e) => commit(e.target.value)}
          disabled={disabled}
          aria-label={`${label} color picker`}
          style={{ width: '40px', height: '38px', padding: '2px', border: '1px solid rgba(61, 18, 32, .16)', borderRadius: '6px', cursor: disabled ? 'not-allowed' : 'pointer', background: 'none', flexShrink: 0, opacity: disabled ? 0.55 : 1 }}
        />
        <input
          type="text"
          value={draft}
          onChange={(e) => commit(e.target.value)}
          placeholder="#RRGGBB"
          maxLength={7}
          disabled={disabled}
          aria-label={`${label} hex value`}
          style={{ flex: 1, minWidth: 0, fontFamily: 'monospace', opacity: disabled ? 0.55 : 1, cursor: disabled ? 'not-allowed' : 'text' }}
        />
      </div>
      {!isValid && (
        <span style={{ fontSize: '0.7rem', color: '#c0392b', marginTop: '0.3rem', display: 'block' }}>
          Enter a valid hex color (e.g. #6fb5ab)
        </span>
      )}
    </div>
  );
}

export default ColorField;
